/**
 * userStorage.js
 *
 * Module central pour gérer les clés localStorage par utilisateur.
 * Chaque module de storage (discovery, progress, roulette, etc.)
 * utilise getStorageKey() pour obtenir une clé préfixée par userId.
 *
 * Quand aucun utilisateur n'est connecté, utilise un préfixe 'anonymous'.
 */

let _currentUserId = null;

const MIGRATION_FLAG = '_storage_migrated_to_user';
const ALL_BASE_KEYS = [
  'discovery_progress',
  'drum-learning-app-progress',
  'roulette_progress',
  'lecture_progression',
  'breaks_progression',
  'ecoute_reproduit_progress'
];

/**
 * Définit l'utilisateur courant. Appelé depuis AuthContext au login/logout.
 * @param {string|null} userId
 */
export function setCurrentUserId(userId) {
  _currentUserId = userId || null;
}

/**
 * Retourne l'userId courant.
 * @returns {string|null}
 */
export function getCurrentUserId() {
  return _currentUserId;
}

/**
 * Construit une clé localStorage préfixée par userId.
 * @param {string} baseKey - La clé de base (ex: 'discovery_progress')
 * @returns {string} Clé préfixée (ex: 'user_abc123_discovery_progress')
 */
export function getStorageKey(baseKey) {
  const prefix = _currentUserId || 'anonymous';
  return `user_${prefix}_${baseKey}`;
}

/**
 * Migration unique des anciennes clés (sans préfixe) vers l'utilisateur courant.
 *
 * Ne migre que si :
 * 1. Les anciennes données existent (clés sans préfixe)
 * 2. Le flag de migration n'a pas encore été posé (= première migration)
 * 3. L'utilisateur courant n'a pas encore de données préfixées
 *
 * Après migration, pose un flag pour empêcher la re-migration vers un autre utilisateur.
 *
 * @returns {string[]} Liste des clés migrées
 */
export function migrateOldDataOnce() {
  if (!_currentUserId) return [];

  // Vérifier si la migration a déjà eu lieu (pour n'importe quel utilisateur)
  const migrationFlag = localStorage.getItem(MIGRATION_FLAG);
  if (migrationFlag) {
    return []; // Déjà migré, ne pas re-migrer
  }

  const migrated = [];

  for (const baseKey of ALL_BASE_KEYS) {
    const oldData = localStorage.getItem(baseKey);
    const newKey = getStorageKey(baseKey);
    const newData = localStorage.getItem(newKey);

    if (oldData && !newData) {
      localStorage.setItem(newKey, oldData);
      migrated.push(baseKey);
    }
  }

  // Poser le flag de migration (avec userId + timestamp)
  localStorage.setItem(MIGRATION_FLAG, JSON.stringify({
    userId: _currentUserId,
    migratedAt: Date.now(),
    keys: migrated
  }));

  if (migrated.length > 0) {
    console.log(`[UserStorage] Migré ${migrated.length} clé(s) vers ${_currentUserId}:`, migrated);
  }

  return migrated;
}
